/**
 * StateLayout — Scoped States Workspace Wrapper (V100)
 *
 * Wraps all state workspace pages.
 * Provides:
 *   - Persistent StateSelector in header
 *   - State context (stateId, geography) via useStateContext
 *   - State-scoped navigation
 *   - Backward compatibility with /states/* routes
 */
import React from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { Map } from "lucide-react";
import { useStateContext } from "@/hooks/useStateContext";
import StateSelector from "@/components/StateSelector";
import OnboardingOverlay from "@/components/OnboardingOverlay";
import NextActionPanel from "@/components/NextActionPanel";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const NAV_ITEMS = [
  { id: "overview",         label: "Overview"         },
  { id: "climate-snapshot", label: "Snapshot"         },
  { id: "aggregated-projects", label: "Projects"      },
  { id: "credentials",      label: "Credentials"      },
  { id: "sdg",              label: "SDG"               },
  { id: "carbon-pathways",  label: "Article 6"        },
  { id: "programmes",       label: "Programmes"       },
  { id: "labeling",         label: "Labeling"         },
  { id: "reports",          label: "Reports"          },
  { id: "financing",        label: "Financing"        },
];

export default function StateLayout() {
  const navigate  = useNavigate();
  const { stateId, geography, stateName, activeState, setActiveState, states } = useStateContext();
  const { stateId: urlStateId, "*": splat } = useParams();

  // Determine active sub-path from URL
  const activePath = (splat || "").split("/")[0] || "overview";

  const handleNavClick = (path) => {
    if (stateId) {
      navigate(`/states/${stateId}/${path}`);
    } else {
      // No stateId — go through StateEntry for dynamic resolution
      navigate(`/states`);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <OnboardingOverlay workspace="states" />
      {/* State-scoped header */}
      <div className="bg-[#0F172A] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Map size={16} className="text-teal-400" />
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">States Governance Workspace</p>
            <p className="text-sm font-bold text-white">{stateName}</p>
            {geography && (
              <p className="text-xs text-slate-500">{geography}</p>
            )}
          </div>
        </div>
        {/* State Selector */}
        <StateSelector
          states={states}
          activeState={activeState}
          onSelect={setActiveState}
        />
      </div>

      {/* State-scoped sub-navigation */}
      <div className="bg-white border-b border-slate-200 px-6 overflow-x-auto">
        <div className="flex gap-0">
          {NAV_ITEMS.map(item => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className={`px-4 py-3 text-xs font-semibold border-b-2 whitespace-nowrap transition-colors ${
                activePath === item.id
                  ? "border-teal-500 text-teal-700"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-200"
              }`}
              data-testid={`state-nav-${item.id}`}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Page content */}
      <div>
        <Outlet />
      </div>
    </div>
  );
}
